import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

import figmaIcon from "@/assets/icons/figma.webp";
import framerIcon from "@/assets/icons/framer.webp";
import lovableIcon from "@/assets/icons/lovable.webp";
import notionIcon from "@/assets/icons/notion.webp";
import awareHealthAppIcon from "@/assets/icons/aware-health-app.webp";
import yukaIcon from "@/assets/icons/yuka.webp";
import pushfitIcon from "@/assets/icons/pushfit.webp";
import chatgptIcon from "@/assets/icons/chatgpt.webp";
import claudeIcon from "@/assets/icons/claude.webp";
import perplexityIcon from "@/assets/icons/perplexity.webp";
import notebooklmIcon from "@/assets/icons/notebooklm.webp";
import substackIcon from "@/assets/icons/substack.webp";
import wetransferIcon from "@/assets/icons/wetransfer.webp";
import dazzcamIcon from "@/assets/icons/dazzcam.webp";
import surflineIcon from "@/assets/icons/surfline.webp";
import spielwerkIcon from "@/assets/icons/spielwerk.webp";
import elevenlabsIcon from "@/assets/icons/elevenlabs.webp";
import alltrailsIcon from "@/assets/icons/alltrails.webp";
import flightyIcon from "@/assets/icons/flighty.webp";

const appIcons = [
  figmaIcon, framerIcon, lovableIcon, notionIcon,
  awareHealthAppIcon, yukaIcon, pushfitIcon,
  chatgptIcon, claudeIcon, perplexityIcon, notebooklmIcon,
  substackIcon, wetransferIcon, dazzcamIcon,
  surflineIcon, spielwerkIcon, elevenlabsIcon, alltrailsIcon, flightyIcon,
];

const ICON_SIZE = 52;
const GAP = 12;
const STEP = ICON_SIZE + GAP;
const PAUSE_MS = 1200;
const SLIDE_MS = 800;

interface AppsSlotCardProps {
  delay?: number;
}

const AppsSlotCard = ({ delay = 0 }: AppsSlotCardProps) => {
  const [step, setStep] = useState(0);
  const [sliding, setSliding] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let paused = true;
    const tick = () => {
      if (paused) {
        paused = false;
        setSliding(true);
        timeoutRef.current = setTimeout(() => {
          setStep((prev) => prev + 1);
          timeoutRef.current = setTimeout(() => {
            setSliding(false);
            paused = true;
            timeoutRef.current = setTimeout(tick, PAUSE_MS);
          }, SLIDE_MS + 50);
        }, 30);
      }
    };
    timeoutRef.current = setTimeout(tick, PAUSE_MS);
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const totalNeeded = step + 8;
  const repeatedIcons: string[] = [];
  while (repeatedIcons.length < totalNeeded) {
    repeatedIcons.push(...appIcons);
  }

  const tiltDeg = sliding ? 6 : 0;

  return (
    <div
      className="shrink-0 anim-fade-in-scale"
      style={{ width: "calc(50% - 6px)", animationDelay: `${delay}s` }}
    >
      <Link
        to="/inspirations/apps"
        className="group flex flex-col items-center justify-center gap-2 rounded-2xl bg-background px-4 py-8 transition-all hover:shadow-sm overflow-hidden relative"
        style={{ minHeight: "200px", maxHeight: "200px" }}
      >
        <div className="text-center text-foreground">
          <p className="text-lg font-bold leading-tight">Apps</p>
          <p className="text-sm font-normal text-muted-foreground">I love</p>
        </div>

        {/* Track container — horizontal mask for fade, but no vertical clipping */}
        <div
          className="mt-2 w-full relative"
          style={{
            height: ICON_SIZE + 16,
            maskImage: "linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)",
          }}
        >
          <motion.div
            className="absolute flex items-center"
            style={{
              gap: GAP,
              left: "50%",
              marginLeft: -(ICON_SIZE / 2),
              top: 4,
            }}
            animate={{
              x: -(step * STEP),
            }}
            transition={{
              duration: SLIDE_MS / 1000,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            {repeatedIcons.map((icon, i) => (
              <motion.img
                key={i}
                src={icon}
                alt=""
                className="shrink-0 rounded-xl object-contain"
                style={{
                  width: ICON_SIZE,
                  height: ICON_SIZE,
                  filter: "drop-shadow(0 0.5px 1.5px rgba(0,0,0,0.07))",
                  transformOrigin: "bottom center",
                }}
                animate={{
                  rotate: tiltDeg,
                }}
                transition={{
                  duration: sliding ? 0.2 : 0.35,
                  ease: sliding ? "easeOut" : [0.34, 1.56, 0.64, 1],
                }}
              />
            ))}
          </motion.div>
        </div>
      </Link>
    </div>
  );
};

export default AppsSlotCard;
